import React, { createContext, useState, useEffect } from 'react';
import {
  ContextData,
  EventDetails,
  InterpolatedData,
  UserDetails,
} from '../types/event_details.types';
import { getEventNestedResource, postEventRegistration } from '../services/event_details.service';
import {
  Speaker,
  Sponsor,
  GetSpeakersResponse,
  GetSponsorsResponse,
} from '../../types/event.types';
import {
  getUserData,
  getUserEmailAddresses,
} from '../../services/current_user/current_user.service';
import { UserDataFullEntity, GetUserEmailAddressesResponse } from '../../types/current_user.types';
import { AlertDataType } from '../../components/react/alert_popup/types';
import { GENERIC_ERROR_MESSAGE } from '../../components/react/alert_popup/constants';
import { REGISTRATION_CALLBACK_PARAM_KEY } from '../constants/event_details.constants';
import { createQueryString } from '../../utilities/utilities';
import { parseSponsorsForState, parseEventForState } from '../utilities/event_details.utilities';
import { hasPermission } from '../../services/permissions/permissions.service';

declare const window: Window & {
  location: {
    origin: string;
    pathname: string;
    search: string;
  };
  plausible: (eventName: string, props?: Record<string, unknown>) => void;
};

const searchParams = new URLSearchParams(window.location.search);
const hasRegistrationCallbackParam = searchParams.has(REGISTRATION_CALLBACK_PARAM_KEY);

const LOGIN_PATH = '/login';
const REGISTER_PATH = '/register';
const CURRENT_PATHNAME = window.location.pathname;

const returnToQueryParam: string = createQueryString({
  return_to: CURRENT_PATHNAME,
});

const CAQueryParams: string = createQueryString({
  payment_page_permalink: 'ca-trial-event',
  return_to: CURRENT_PATHNAME,
});

const returnToQueryParamForCmMembership: string = createQueryString({
  return_to: CURRENT_PATHNAME + createQueryString({ skip_optional_steps: 'true' }),
});

interface Props extends InterpolatedData {
  children: React.ReactNode;
}

const EventDetailsContext = createContext<ContextData>(null);

const EventDetailsContextProvider = ({ children, ...props }: Props): JSX.Element => {
  const [alertData, setAlertData] = useState<AlertDataType | null>(null);
  const [activeTab, setActiveTab] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
  const [event, setEvent] = useState<EventDetails>(parseEventForState(props.event));
  const [userDetails, setUserDetails] = useState<UserDetails>({
    email: null,
    id: null,
    firstName: null,
    lastName: null,
    jobTitle: null,
    location: null,
    company: null,
  });
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[] | Array<Sponsor[]>>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [showConfirmInfo, setShowConfirmInfo] = useState<boolean>(false);
  const [questionForSpeaker, setQuestionForSpeaker] = useState<string>('');
  const { required_privilege } = props.event;

  // If event requires privilege, set the redirection to `/register/PRO` or `/register/CA` as needed

  const redirectionUrl = required_privilege?.constant_name
    ? `${REGISTER_PATH}/${
      required_privilege.constant_name.includes('PRO')
        ? `PRO${returnToQueryParam}`
        : `CA${CAQueryParams}`
    }`
    : `${LOGIN_PATH}${returnToQueryParam}`;

  const loginRedirectionUrl = `${LOGIN_PATH}${returnToQueryParam}`;

  const registerRedirectionUrl = `${REGISTER_PATH}/${
    required_privilege
      ? required_privilege.constant_name.includes('PRO')
        ? `PRO${returnToQueryParam}`
        : `CA${CAQueryParams}`
      : `CM${returnToQueryParamForCmMembership}`
  }`;

  const [hasCorrectPrivilege, setHasCorrectPrivilege] = useState<boolean>(false);

  // Whenever the user scrolls past the menu bar, the FixedBottomCta should
  // be visible at the bottom of the page
  useEffect(() => {
    if (isLoading) return;
    const fixedBottomContainer = document.getElementById('fixed-bottom-cta');
    if (!fixedBottomContainer) return;
    const bodyContent = document.querySelector('body');
    const ctaSection = document.getElementById('cta-section');

    document.addEventListener('scroll', () => {
      const { classList } = fixedBottomContainer;
      // make FixedBottomCta visible and add extra padding
      if (ctaSection?.getBoundingClientRect()?.top <= 0) {
        classList.replace('invisible', 'visible');
        // just applied on big screens since has a d-none class
        if (window.innerWidth > 766) {
          bodyContent.style.paddingBottom = '96px';
        }

        return null;
      }
      // hide FixedBottomCta and remove extra padding
      if (classList.contains('visible')) {
        classList.replace('visible', 'invisible');
        bodyContent.style.paddingBottom = '0';
      }
    });
  }, [isLoading]);

  const triggerRegistration = (): Promise<void> => {
    setIsLoading(true);
    return postEventRegistration(event?.id, userDetails.id, questionForSpeaker.trim())
      .then(() => {
        setShowConfirmationModal(true);
        setEvent({
          ...event,
          is_current_user_attending: true,
        });

        window.plausible?.('event_registration', {
          props: {
            event_privilege: required_privilege?.constant_name || 'none',
          },
        });
      })
      .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE }))
      .finally(() => setIsLoading(false));
  };

  const onCtaClick = async (): Promise<void | null> => {
    if (event?.is_current_user_attending || isLoading) return null;

    if (window.plausible) {
      await window.plausible('click', {
        props: {
          intention: 'register for event',
        },
      });
    }

    // If users are UA (needs to login) OR if AA users do not have correct privilege, redirect them
    if (!isSignedIn || !hasCorrectPrivilege) {
      window.location.href = redirectionUrl;
      return null;
    }

    return setShowConfirmInfo(true);
  };

  const onLoginCtaClick = async (): Promise<void> => {
    if (window.plausible) {
      await window.plausible('click', {
        props: {
          intention: 'login to register for event',
        },
      });
    }

    window.location.href = loginRedirectionUrl;
  };

  const onRegisterCtaClick = async (): Promise<void> => {
    if (window.plausible) {
      await window.plausible('click', {
        props: {
          intention: 'sign up to register for event',
        },
      });
    }

    window.location.href = registerRedirectionUrl;
  };

  useEffect((): void => {
    const eventId = props.event.id;
    setIsLoading(true);

    const onGetUserDataError = (error: { status?: number }) => {
      // If user is not logged in in his/her current device
      if ([401, 403].includes(error.status)) {
        setIsSignedIn(false);
        return;
      }

      setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE });
    };

    // On `/api/current_user?detail_level=full` success, chain request to get email addresses
    // from current_user
    const onGetUserDataSuccess = (response: UserDataFullEntity) => {
      const {
        id,
        first_name,
        last_name,
        headline,
        place,
        user_declared_company,
        user_declared_company_name,
      } = response;
      // Upon receiving the user data from the BE, we trim first_name, last_name and headline in
      // order to remove empty spaces from the beginning and end of the string. We want to avoid
      // empty spaces at the begining or at the end of such fields. There is no good use case
      // to have them. Note that spaces are still allowed in the middle of the string.
      const details: UserDetails = {
        id,
        firstName: first_name?.trim() || null,
        lastName: last_name?.trim() || null,
        jobTitle: headline?.trim() || null,
        location: place,
        email: null,
        company: user_declared_company
          ? { id: user_declared_company.id, text: user_declared_company.name }
          : user_declared_company_name
            ? { id: 'new-id-1', text: user_declared_company_name, customOption: true }
            : null,
      };

      return getUserEmailAddresses(response.id)
        .then((emailAddresses: GetUserEmailAddressesResponse) => {
          setIsSignedIn(true);

          setUserDetails({
            ...details,
            email: emailAddresses.data.find((email) => email.is_primary)?.email,
          });
        })
        .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE }));
    };

    const onInitialRequestsCompletion = (
      responses: [
        PromiseSettledResult<GetSpeakersResponse>,
        PromiseSettledResult<GetSponsorsResponse>,
        PromiseSettledResult<UserDataFullEntity>
      ]
    ) => {
      // If GET sponsors or GET speakers failed, show an error alert
      if ([responses[0], responses[1]].some((result) => result.status === 'rejected')) {
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE });
      }

      const receivedSpeakers = responses[0].status === 'fulfilled' ? responses[0].value?.data : [];
      const receivedSponsors = responses[1].status === 'fulfilled' ? responses[1].value?.data : [];
      setSpeakers(receivedSpeakers as Speaker[]);
      setSponsors(parseSponsorsForState(receivedSponsors as Sponsor[]));

      if (responses[2].status === 'fulfilled') {
        return onGetUserDataSuccess(responses[2].value);
      } else if (responses[2].status === 'rejected') {
        return onGetUserDataError(responses[2].reason);
      }
    };

    // Initial requests to get user data, speakers and sponsors
    // We can run both GET user data, GET Event Speakers and GET Event Sponsors in parallel
    // hence the use of Promise.allSettled
    Promise.allSettled([
      getEventNestedResource('speakers', eventId),
      getEventNestedResource('sponsors', eventId),
      getUserData('full'),
    ])
      .then(onInitialRequestsCompletion)
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Check to see if logged in user holds the correct privilege
  useEffect(() => {
    const { is_current_user_attending } = event;

    // If current user is attending OR if user is not logged in
    if (is_current_user_attending || !isSignedIn) {
      setHasCorrectPrivilege(false);
      return;
    }

    // If user is logged in, not attending yet but event does not require privilege
    if (required_privilege === null) {
      setHasCorrectPrivilege(true);
      return;
    }

    // If user is logged in AND event requires specific privilege
    if (required_privilege?.constant_name) {
      hasPermission(required_privilege.constant_name)
        .then(setHasCorrectPrivilege)
        .catch(Promise.reject);
    }
  }, [event, isSignedIn]);

  const exposedContextData = {
    isLoading,
    event,
    userDetails,
    isSignedIn,
    activeTab,
    setActiveTab,
    showConfirmationModal,
    setShowConfirmationModal,
    speakers,
    sponsors,
    hasRegistrationCallbackParam,
    hasCorrectPrivilege,
    triggerRegistration,
    onCtaClick,
    onLoginCtaClick,
    onRegisterCtaClick,
    showConfirmInfo,
    setShowConfirmInfo,
    questionForSpeaker,
    setQuestionForSpeaker,
    alertData,
    setAlertData,
  };

  return (
    <EventDetailsContext.Provider value={exposedContextData}>
      {children}
    </EventDetailsContext.Provider>
  );
};

export { EventDetailsContext, EventDetailsContextProvider };
